import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Play, CheckCircle, Lock, ArrowLeft, ArrowRight, FileText, Download, X } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const CourseVideoPlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewLessonId = searchParams.get('lesson');
  const reviewTimestamp = useMemo(() => {
    const raw = searchParams.get('t');
    if (raw === null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [searchParams]);
  const [reviewBannerVisible, setReviewBannerVisible] = useState(false);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]); // Always initialize as empty array
  const [currentLesson, setCurrentLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoWatched, setVideoWatched] = useState(false);
  const [error, setError] = useState(null);
  const [watchState, setWatchState] = useState({ lessonId: null, seconds: 0 });
  const watchedMarkedRef = useRef(false); // Prevent double-firing handleVideoEnd

  // Required on-page watch time before the "I've finished watching" confirm
  // button unlocks. Lesson durations in the DB are placeholders (all 45 min),
  // so gating on the full length forced people to sit through more time than
  // the real video runs. We require the SMALLER of 60% of the stored length or
  // a 15-minute hard cap, so the gate is always achievable within one play.
  const getRequiredSeconds = (lesson) => {
    const full = (lesson?.duration_minutes || 1) * 60;
    return Math.max(30, Math.min(Math.round(full * 0.6), 15 * 60));
  };

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  // ----- Watch-time tracking (replaces the broken 5-second onLoad hack) -----
  // When the current lesson changes, seed watchState from localStorage so
  // refreshes / navigations preserve progress. If the backend already marked
  // the video as watched, treat as fully watched immediately so the exam
  // stays unlocked for users who completed before this change.
  useEffect(() => {
    if (!currentLesson) return;
    watchedMarkedRef.current = false;
    const required = getRequiredSeconds(currentLesson);
    const lp = progress?.lessons_progress?.[currentLesson.id];
    const backendWatched = Boolean(lp?.video_watched || lp?.exam_passed);
    if (backendWatched) {
      setWatchState({ lessonId: currentLesson.id, seconds: required });
      setVideoWatched(true);
      watchedMarkedRef.current = true;
      return;
    }
    let persisted = 0;
    try {
      const raw = parseInt(localStorage.getItem(`watch_seconds_${currentLesson.id}`) || '0', 10);
      persisted = Number.isFinite(raw) && raw >= 0 ? raw : 0;
    } catch (_) { /* localStorage may be unavailable */ }
    setWatchState({ lessonId: currentLesson.id, seconds: persisted });
  }, [currentLesson?.id, progress]);

  // Tick once per second while a lesson is selected and the tab is visible.
  // Persist progress every tick so refreshes don't wipe it.
  useEffect(() => {
    if (!currentLesson) return;
    const lessonId = currentLesson.id;
    const required = getRequiredSeconds(currentLesson);
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      setWatchState((prev) => {
        if (prev.lessonId !== lessonId) return prev; // ignore stale ticks across lesson swaps
        if (prev.seconds >= required) return prev;
        const next = prev.seconds + 1;
        try {
          localStorage.setItem(`watch_seconds_${lessonId}`, String(next));
        } catch (_) { /* localStorage may be unavailable */ }
        return { lessonId, seconds: next };
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [currentLesson?.id]);

  // No auto-mark: the user explicitly confirms with the "I've finished
  // watching" button once the required watch time is reached (see render).

  const fetchCourseData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      // Fetch course
      const courseRes = await fetch(`${backendUrl}/api/courses/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (courseRes.ok) {
        const courseData = await courseRes.json();
        setCourse(courseData);
      }

      // Fetch lessons with comprehensive error handling
      try {
        const lessonsRes = await fetch(`${backendUrl}/api/video-lessons/course/${courseId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        console.log('Lessons API response status:', lessonsRes.status);
        
        if (lessonsRes.ok) {
          const lessonsData = await lessonsRes.json();
          console.log('Lessons data received (raw):', lessonsData);
          console.log('Type of lessonsData:', typeof lessonsData);
          console.log('Is lessonsData an array?:', Array.isArray(lessonsData));
          
          // Handle different response formats
          let lessonsList = [];
          if (Array.isArray(lessonsData)) {
            // If response is directly an array
            lessonsList = lessonsData;
            console.log('Format: Direct array');
          } else if (lessonsData && typeof lessonsData === 'object') {
            if (Array.isArray(lessonsData.lessons)) {
              // If response is {lessons: [...]}
              lessonsList = lessonsData.lessons;
              console.log('Format: Object with lessons array');
            } else if (Array.isArray(lessonsData.data)) {
              // If response is {data: [...]}
              lessonsList = lessonsData.data;
              console.log('Format: Object with data array');
            } else {
              console.error('Unknown response format. Keys:', Object.keys(lessonsData));
            }
          }
          
          console.log('Final lessons list:', lessonsList);
          console.log('Lessons count:', lessonsList.length);
          
          // Ensure it's an array before setting
          if (Array.isArray(lessonsList)) {
            setLessons(lessonsList);
            if (lessonsList.length > 0) {
              // Preserve current selection across refreshes (fixes "jumps back
              // to Lesson 1" bug after progress updates). Only auto-select on
              // first load or when a deep-link ?lesson= is present.
              setCurrentLesson((prev) => {
                if (prev) {
                  const same = lessonsList.find((l) => l.id === prev.id);
                  return same || prev;
                }
                const target = reviewLessonId
                  ? lessonsList.find((l) => l.id === reviewLessonId)
                  : null;
                if (target && reviewTimestamp !== null) {
                  setReviewBannerVisible(true);
                }
                return target || lessonsList[0];
              });
            }
          } else {
            console.error('lessonsList is not an array:', lessonsList);
            setLessons([]);
            setError('Invalid lessons data format');
          }
        } else {
          const errorData = await lessonsRes.json().catch(() => ({}));
          console.error('Failed to fetch lessons:', lessonsRes.status, errorData);
          setLessons([]);
          setError(`Failed to load lessons: ${lessonsRes.status}`);
        }
      } catch (lessonsError) {
        console.error('Error fetching lessons:', lessonsError);
        setLessons([]);
        setError('Failed to load lessons');
      }

      // Fetch progress (backend route: GET /api/course-progress/{course_id})
      const progressRes = await fetch(`${backendUrl}/api/course-progress/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (progressRes.ok) {
        const progressData = await progressRes.json();
        setProgress(progressData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching course data:', error);
      setError('Failed to load course');
      setLessons([]); // Ensure lessons is always an array
      setLoading(false);
    }
  };

  const handleVideoEnd = async () => {
    setVideoWatched(true);
    
    // Update progress
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await fetch(`${backendUrl}/api/course-progress/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          lesson_id: currentLesson.id,
          video_watched: true,
          video_watch_percentage: 100
        })
      });
      
      fetchCourseData(); // Refresh progress
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleTakeExam = () => {
    navigate(`/exam/${currentLesson.id}`);
  };

  const handleDownloadResource = async (resource) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const fullUrl = resource.file_url.startsWith('http') 
        ? resource.file_url 
        : `${backendUrl}${resource.file_url}`;
      
      const response = await fetch(fullUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resource.file_name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleLessonSelect = (lesson) => {
    if (!Array.isArray(lessons) || lessons.length === 0) return;
    
    const lessonProgress = progress?.lessons_progress?.[lesson.id];
    
    // Reuse the same gating predicate so selection rules match the lock UI.
    if (isLessonUnlocked(lesson)) {
      setCurrentLesson(lesson);
      setVideoWatched(lessonProgress?.video_watched || false);
      // Clear the review banner when the user manually picks a different lesson
      if (reviewBannerVisible && lesson.id !== reviewLessonId) {
        setReviewBannerVisible(false);
      }
    }
  };

  const dismissReviewBanner = () => {
    setReviewBannerVisible(false);
    const next = new URLSearchParams(searchParams);
    next.delete('lesson');
    next.delete('t');
    setSearchParams(next, { replace: true });
  };

  const formatReviewTimestamp = (secs) => {
    if (secs === null || secs === undefined) return '';
    const total = Math.max(0, Math.floor(secs));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  };

  const isLessonUnlocked = (lesson) => {
    if (!Array.isArray(lessons) || lessons.length === 0) return false;

    const lessonIndex = lessons.findIndex(l => l.id === lesson.id);

    // First lesson is always unlocked
    if (lessonIndex === 0) return true;

    // Admin / mentor see everything (for testing & content management)
    const isAdmin = localStorage.getItem('user_role') === 'admin' ||
                    localStorage.getItem('user_role') === 'mentor';
    if (isAdmin) return true;

    // STRICT gating: every subsequent lesson requires the PREVIOUS lesson's
    // exam to be PASSED at 100%. Watching the video is NOT enough.
    // (The video-watched fallback only kicks in if the previous lesson has
    // NO exam at all — and every lesson in production now has a hardcoded
    // 12-question mastery exam.)
    const previousLesson = lessons[lessonIndex - 1];
    const previousProgress = progress?.lessons_progress?.[previousLesson.id];
    const previousHasExam = Boolean(previousLesson?.has_exam);

    if (previousHasExam) {
      return previousProgress?.exam_passed === true;
    }
    return (
      previousProgress?.video_watched === true ||
      (previousProgress?.video_watch_percentage || 0) >= 80
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-red-900 mb-4">Error Loading Course</h2>
            <p className="text-red-700 mb-6">{error}</p>
            <div className="flex space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold"
              >
                Reload Page
              </button>
              <button
                onClick={() => navigate('/courses')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-3 rounded-lg font-semibold"
              >
                Back to Courses
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/courses')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Courses
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{course?.title || 'Course'}</h1>
          <p className="text-gray-600 mt-2">{course?.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {currentLesson ? (
                <>
                  {reviewBannerVisible && reviewTimestamp !== null && currentLesson.id === reviewLessonId && (
                    <div
                      data-testid="exam-review-banner"
                      className="flex items-center justify-between gap-3 bg-amber-50 border-b border-amber-200 px-4 py-3"
                    >
                      <div className="flex items-center gap-2 text-amber-900">
                        <Play className="w-4 h-4 flex-shrink-0" />
                        <p className="text-sm">
                          <span className="font-semibold">Review tip:</span>{' '}
                          Scrub the video to{' '}
                          <span
                            data-testid="exam-review-timestamp"
                            className="font-mono font-semibold bg-amber-100 px-1.5 py-0.5 rounded"
                          >
                            {formatReviewTimestamp(reviewTimestamp)}
                          </span>{' '}
                          — this is the part that covers the question you missed.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={dismissReviewBanner}
                        className="p-1 text-amber-700 hover:bg-amber-100 rounded transition-colors flex-shrink-0"
                        aria-label="Dismiss review tip"
                        data-testid="exam-review-banner-dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="aspect-video bg-black">
                    <iframe
                      src={`https://drive.google.com/file/d/${currentLesson.drive_file_id}/preview`}
                      className="w-full h-full"
                      allow="autoplay"
                      allowFullScreen
                      title={currentLesson.title}
                    ></iframe>
                  </div>
                  
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {currentLesson.title}
                    </h2>
                    <p className="text-gray-600 mb-6">
                      {currentLesson.description}
                    </p>

                    {/* Lesson Resources Section */}
                    {currentLesson.resources && currentLesson.resources.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                          <FileText className="w-5 h-5 mr-2" />
                          Lesson Materials
                        </h3>
                        <div className="space-y-2">
                          {currentLesson.resources.map((resource) => (
                            <div 
                              key={resource.id}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100"
                            >
                              <div className="flex items-center space-x-3">
                                <FileText className="w-5 h-5 text-blue-600" />
                                <div>
                                  <p className="font-medium text-gray-900">{resource.title}</p>
                                  <p className="text-sm text-gray-500">{resource.file_name}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDownloadResource(resource)}
                                className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {videoWatched ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6" data-testid="video-watched-banner">
                        <div className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                          <span className="text-green-800 font-medium">Lesson complete — the exam is unlocked!</span>
                        </div>
                      </div>
                    ) : (
                      (() => {
                        const required = getRequiredSeconds(currentLesson);
                        const liveSeconds = watchState.lessonId === currentLesson.id ? watchState.seconds : 0;
                        const elapsed = Math.min(liveSeconds, required);
                        const pct = Math.round((elapsed / required) * 100);
                        const met = elapsed >= required;
                        const fmt = (s) => {
                          const m = Math.floor(s / 60);
                          const r = s % 60;
                          return `${m}:${String(r).padStart(2, '0')}`;
                        };
                        return (
                          <div className="mb-6">
                            <div
                              className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4"
                              data-testid="watch-progress"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-purple-900">
                                  Watch progress
                                </span>
                                <span className="text-sm font-mono text-purple-700" data-testid="watch-progress-counter">
                                  {fmt(elapsed)} / {fmt(required)} ({pct}%)
                                </span>
                              </div>
                              <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden">
                                <div
                                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${pct}%` }}
                                ></div>
                              </div>
                              <p className="text-xs text-purple-700 mt-2">
                                {met
                                  ? "Great — you've watched enough of this lesson. Confirm below to unlock the exam."
                                  : 'Keep this lesson playing (with the tab active). Once you\'ve watched enough, you can confirm and unlock the exam.'}
                              </p>
                            </div>
                            <button
                              onClick={handleVideoEnd}
                              disabled={!met}
                              data-testid="confirm-watched-btn"
                              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                            >
                              <CheckCircle className="w-5 h-5" />
                              {met
                                ? "I've finished watching — unlock the exam"
                                : `Watch a bit more to confirm (${pct}%)`}
                            </button>
                          </div>
                        );
                      })()
                    )}

                    <button
                      onClick={handleTakeExam}
                      disabled={!videoWatched}
                      data-testid="take-exam-btn"
                      className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      {videoWatched ? 'Take Exam' : 'Complete the lesson to unlock the exam'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="aspect-video bg-gray-900 flex items-center justify-center">
                  <p className="text-white">No lesson selected</p>
                </div>
              )}
            </div>
          </div>

          {/* Lesson List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Course Content</h3>
              
              <div className="space-y-2">
                {Array.isArray(lessons) && lessons.length > 0 ? lessons.map((lesson, index) => {
                  const unlocked = isLessonUnlocked(lesson);
                  const lessonProgress = progress?.lessons_progress?.[lesson.id];
                  const completed = lessonProgress?.exam_passed || false;
                  const isActive = currentLesson?.id === lesson.id;

                  return (
                    <button
                      key={lesson.id}
                      onClick={() => handleLessonSelect(lesson)}
                      disabled={!unlocked}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isActive
                          ? 'border-purple-600 bg-purple-50'
                          : unlocked
                          ? 'border-gray-200 hover:border-purple-300 bg-white'
                          : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-semibold text-gray-500">
                              Lesson {index + 1}
                            </span>
                            {completed && (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            )}
                            {!unlocked && (
                              <Lock className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <p className={`font-medium ${
                            unlocked ? 'text-gray-900' : 'text-gray-400'
                          }`}>
                            {lesson.title}
                          </p>
                          {lesson.duration_minutes && (
                            <p className="text-xs text-gray-500 mt-1">
                              {lesson.duration_minutes} min
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                }) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No lessons available yet.</p>
                  </div>
                )}
              </div>

              {/* Progress Summary */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Overall Progress</span>
                  <span className="text-sm font-bold text-purple-600">
                    {Math.round(progress?.overall_completion_percentage || 0)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress?.overall_completion_percentage || 0}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {progress?.lessons_completed || 0} of {progress?.total_lessons || 0} lessons completed
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseVideoPlayer;